import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, Route, Routes, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../api/client";
import type { Me } from "../api/types";
import { Field } from "../components/Layout";
import { ThemeToggle } from "../components/ThemeToggle";
import { QueryState, SkeletonBlock } from "../components/States";
import { dateTime } from "../lib/format";
import { currentPosition } from "./geo";
import { newId } from "./queue";
import { Recorder } from "./Recorder";
import { mb, useSync } from "./useSync";

interface Options { road_classes: { value: string; label: string }[]; agencies: string[]; surfaces: string[]; locations: Record<string, Record<string, string[]>> }
interface MySurvey { survey_uuid: string; status: string; start_time: string; road_name: string; route_number: string; county: string; km: number }

export function FieldBar({ title, back }: { title: string; back?: string }) {
  return (
    <header className="field-bar">
      {back ? <Link to={back} className="field-back" aria-label="Back">‹</Link> : <span className="brand-name" style={{ fontSize: 20 }}>Roadrics<b>AI</b></span>}
      <span className="field-bar-title">{title}</span>
      <Link to="/" className="field-link">Office view</Link>
      <ThemeToggle className="field-theme" />
    </header>
  );
}

export function SyncCard({ surveyUuid }: { surveyUuid?: string }) {
  const s = useSync(surveyUuid);
  return (
    <section className="field-card">
      <div className="field-stats">
        <div><span>Network</span><b className={s.online ? "ok" : "bad"}>{s.online ? "Online" : "Offline"}</b></div>
        <div><span>Waiting to sync</span><b>{s.pending.total}</b></div>
        <div><span>Phone storage</span><b>{s.storage.quota ? `${mb(s.storage.usage)}` : "—"}</b></div>
      </div>
      {s.pending.total ? (
        <button className="btn btn-primary field-btn" disabled={!s.online || s.busy} onClick={s.sync}>
          {s.busy ? "Syncing…" : s.online ? `Sync ${s.pending.total} item${s.pending.total === 1 ? "" : "s"} now` : "Will sync when back online"}
        </button>
      ) : null}
      {s.last?.error ? <p className="field-msg bad" role="alert">{s.last.error.message}</p> : null}
    </section>
  );
}

function FieldHome() {
  const q = useQuery({ queryKey: ["field", "surveys"], queryFn: () => api<{ items: MySurvey[] }>("/api/field/surveys"), refetchInterval: 30000 });
  return (
    <div className="field-page">
      <FieldBar title="Field surveys" />
      <main className="field-main">
        <Link to="/field/start" className="btn btn-good field-btn field-cta">＋ Start a new road survey</Link>
        <SyncCard />
        <h2 className="field-h">Your surveys</h2>
        <QueryState query={q} skeleton={<SkeletonBlock rows={3} height={56} />}>
          {(d) => d.items.length ? (
            <ul className="field-list">
              {d.items.map((s) => (
                <li key={s.survey_uuid}>
                  <Link to={`/field/${s.survey_uuid}`} className="field-item">
                    <span><b>{s.road_name}</b><small>{[s.route_number, s.county, `${s.km.toFixed(2)} km`].filter(Boolean).join(" · ")}</small><small>Started {dateTime(s.start_time)}</small></span>
                    <span className={`chip ${s.status === "ACTIVE" ? "unreviewed" : "verified"}`}>{s.status === "ACTIVE" ? "Recording" : "Done"}</span>
                  </Link>
                </li>
              ))}
            </ul>
          ) : <p className="field-msg">No surveys yet. Start one when you're at the beginning of the road section.</p>}
        </QueryState>
      </main>
    </div>
  );
}

function FieldStart({ me }: { me: Me }) {
  const nav = useNavigate();
  const opts = useQuery({ queryKey: ["field", "options"], queryFn: () => api<Options>("/api/field/options"), staleTime: Infinity });
  const [f, setF] = useState({
    road_class: "", route_number: "", road_name: "", agency: "", surface: "", start_location_name: "", end_location_name: "",
    county: "", constituency: "", ward: "", surveyor: me.full_name, notes: "", pilot: false, pilot_target_km: "5",
  });
  const [pos, setPos] = useState<GeolocationPosition | null>(null);
  const [gpsMsg, setGpsMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setF((x) => ({ ...x, [k]: e.target.type === "checkbox" ? (e.target as HTMLInputElement).checked : e.target.value }));

  const loc = opts.data?.locations ?? {};
  const constituencies = useMemo(() => Object.keys(loc[f.county] ?? {}), [loc, f.county]);
  const wards = useMemo(() => loc[f.county]?.[f.constituency] ?? [], [loc, f.county, f.constituency]);
  useEffect(() => setF((x) => ({ ...x, constituency: "", ward: "" })), [f.county]);
  useEffect(() => setF((x) => ({ ...x, ward: "" })), [f.constituency]);

  const capture = async () => {
    setGpsMsg("Getting a GPS fix…");
    try {
      const p = await currentPosition();
      setPos(p);
      setGpsMsg(null);
    } catch (e) {
      setGpsMsg(e instanceof Error ? e.message : "GPS failed.");
    }
  };

  const start = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pos) { setError("Capture the start GPS first."); return; }
    setBusy(true);
    setError(null);
    try {
      const c = pos.coords;
      const r = await api<{ survey_uuid: string }>("/api/field/surveys", {
        method: "POST",
        json: {
          client_event_uuid: newId(), ...f, pilot_target_km: f.pilot ? Number(f.pilot_target_km) : null,
          lat: c.latitude, lon: c.longitude, accuracy: c.accuracy, altitude: c.altitude, speed: c.speed, heading: c.heading,
          captured_at_device: new Date(pos.timestamp).toISOString(),
        },
      });
      nav(`/field/${r.survey_uuid}?autostart=1`, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "The survey could not be started.");
      setBusy(false);
    }
  };

  const ready = f.road_class && f.road_name.trim() && f.agency && f.surface && f.county && f.constituency && f.ward && pos;

  return (
    <div className="field-page">
      <FieldBar title="New road survey" back="/field" />
      <main className="field-main">
        {!navigator.onLine ? <p className="field-msg bad">You're offline. Starting a survey needs a connection once; after that, recording works offline.</p> : null}
        <QueryState query={opts} skeleton={<SkeletonBlock rows={8} height={44} />}>
          {(o) => (
            <form className="stack" style={{ gap: 20 }} onSubmit={start}>
              <section className="field-card stack">
                <h2 className="field-h">1. The road</h2>
                <Field label="Road name" htmlFor="fs-name"><input id="fs-name" className="input field-input" required value={f.road_name} onChange={set("road_name")} placeholder="e.g. Kiambu – Limuru Road" /></Field>
                <div className="field-two">
                  <Field label="KRB class" htmlFor="fs-class">
                    <select id="fs-class" className="select field-input" required value={f.road_class} onChange={set("road_class")}>
                      <option value="">Choose…</option>{o.road_classes.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                  </Field>
                  <Field label="Route code" htmlFor="fs-route" hint="e.g. B8"><input id="fs-route" className="input field-input" value={f.route_number} onChange={set("route_number")} autoCapitalize="characters" /></Field>
                </div>
                <div className="field-two">
                  <Field label="Agency" htmlFor="fs-agency">
                    <select id="fs-agency" className="select field-input" required value={f.agency} onChange={set("agency")}><option value="">Choose…</option>{o.agencies.map((a) => <option key={a}>{a}</option>)}</select>
                  </Field>
                  <Field label="Surface" htmlFor="fs-surface">
                    <select id="fs-surface" className="select field-input" required value={f.surface} onChange={set("surface")}><option value="">Choose…</option>{o.surfaces.map((a) => <option key={a}>{a}</option>)}</select>
                  </Field>
                </div>
                <Field label="County" htmlFor="fs-county">
                  <select id="fs-county" className="select field-input" required value={f.county} onChange={set("county")}><option value="">Choose…</option>{Object.keys(loc).map((c) => <option key={c}>{c}</option>)}</select>
                </Field>
                <div className="field-two">
                  <Field label="Constituency" htmlFor="fs-cons">
                    <select id="fs-cons" className="select field-input" required disabled={!f.county} value={f.constituency} onChange={set("constituency")}><option value="">Choose…</option>{constituencies.map((c) => <option key={c}>{c}</option>)}</select>
                  </Field>
                  <Field label="Ward" htmlFor="fs-ward">
                    <select id="fs-ward" className="select field-input" required disabled={!f.constituency} value={f.ward} onChange={set("ward")}><option value="">Choose…</option>{wards.map((w) => <option key={w}>{w}</option>)}</select>
                  </Field>
                </div>
                <div className="field-two">
                  <Field label="Starts at" htmlFor="fs-sn"><input id="fs-sn" className="input field-input" value={f.start_location_name} onChange={set("start_location_name")} placeholder="Landmark" /></Field>
                  <Field label="Ends at" htmlFor="fs-en"><input id="fs-en" className="input field-input" value={f.end_location_name} onChange={set("end_location_name")} placeholder="Landmark" /></Field>
                </div>
                <Field label="Surveyor" htmlFor="fs-who"><input id="fs-who" className="input field-input" value={f.surveyor} onChange={set("surveyor")} /></Field>
                <label className="check"><input type="checkbox" checked={f.pilot} onChange={set("pilot")} />Pilot drive with a target distance</label>
                {f.pilot ? (
                  <Field label="Target (km)" htmlFor="fs-target"><select id="fs-target" className="select field-input" value={f.pilot_target_km} onChange={set("pilot_target_km")}>{[5, 6, 7, 8, 9, 10].map((k) => <option key={k}>{k}</option>)}</select></Field>
                ) : null}
                <Field label="Notes" htmlFor="fs-notes"><textarea id="fs-notes" className="textarea" value={f.notes} onChange={set("notes")} /></Field>
              </section>

              <section className="field-card stack">
                <h2 className="field-h">2. Start point</h2>
                <p className="muted">Stop the vehicle at the start of the section, then capture the GPS.</p>
                {pos ? <p className="field-msg ok">GPS captured: {pos.coords.latitude.toFixed(6)}, {pos.coords.longitude.toFixed(6)} · ±{Math.round(pos.coords.accuracy)} m</p> : null}
                {gpsMsg ? <p className="field-msg">{gpsMsg}</p> : null}
                <button type="button" className="btn field-btn" onClick={capture}>{pos ? "Capture again" : "Capture start GPS"}</button>
              </section>

              {error ? <p className="field-msg bad" role="alert">{error}</p> : null}
              <button type="submit" className="btn btn-good field-btn field-cta" disabled={!ready || busy}>{busy ? "Starting…" : "Start survey and recording"}</button>
            </form>
          )}
        </QueryState>
      </main>
    </div>
  );
}

export function FieldApp({ me }: { me: Me }) {
  return (
    <Routes>
      <Route index element={<FieldHome />} />
      <Route path="start" element={<FieldStart me={me} />} />
      <Route path=":surveyId" element={<Recorder />} />
      <Route path="*" element={<Navigate to="/field" replace />} />
    </Routes>
  );
}
